import { createReducer } from 'typesafe-actions'
import { combineReducers } from 'redux'
import { EMPTY_RESOURCES, HistoryData, HistoryResources, } from '../../../responses/history'
import { payloadReducer } from '../../../utils/redux-utils'
import { RequestStatus } from '../../../responses/response'
import type { ID } from '../../../types/UtilityTypes'
import { entityIdPredicate, entityModify } from '../../../utils/entity-utils'
import type { ServerTask } from '../../../responses/tasks'
import { EMPTY_PROJECT, Project, ServerProject } from '../../../responses/projects'
import type { ProjectWorkObject } from '../../../responses/objects'
import * as R from 'fp-ts/es6/Record'
import { projectActions } from '../../data/project/project-actions'
import { historyRead } from '../../../utils/lenses'
import { taskActions } from '../../data/task/task-actions'
import { NotificationType } from '../../../responses/notification'
import { notificationActions } from '../notification'
import { concatResources, historyActions, HistoryFilters } from './history-actions'
import type { Comment } from '../../../responses/comments'
import { getLastSemigroup } from 'fp-ts/es6/Semigroup'
import { array } from 'fp-ts/es6/Array'
import { ADTType, makeADT, ofType } from '@morphic-ts/adt'

const PAGE_SIZE = 50

const mapComments = (comments: Comment[]): Record<ID, Comment> => R.fromFoldableMap(getLastSemigroup<Comment>(), array)(
    comments, comment => [comment.id.toString(), comment],
)

export const CommentEditState = makeADT('type')({
    none: ofType<{ type: 'none' }>(),
    update: ofType<{ type: 'update', comment: Comment }>(),
    delete: ofType<{ type: 'delete', comment: Comment }>()
})

export interface HistoryState {
    show: boolean,
    quote: Comment | null | undefined,
    editState: ADTType<typeof CommentEditState>,
    status: RequestStatus,
    sendStatus: RequestStatus,
    filters: HistoryFilters,
    offset: number,
    resources: HistoryResources,
    data: HistoryData[],
    owner: ServerProject | Project | ProjectWorkObject | ServerTask,
    total: number,
    editor: never[], // for editor state clear
}

export const historyReducer = combineReducers<HistoryState>({
    show: createReducer<boolean>(true)
        .handleAction(historyActions.toggle, state => !state),
    quote: createReducer<Comment | null | undefined>(null)
        .handleAction(historyActions.quote, payloadReducer)
        .handleAction(historyActions.startEdit, (_, action) => action.payload.ref)
        .handleAction([historyActions.comment.success, historyActions.cancelEdit, historyActions.delete.success], () => null),
    editState: createReducer<ADTType<typeof CommentEditState>>(CommentEditState.as.none({}))
        .handleAction(historyActions.startDelete, (_, {payload}) => CommentEditState.as.delete({comment: payload}))
        .handleAction(historyActions.startEdit, (_, {payload}) => CommentEditState.as.update({comment: payload}))
        .handleAction([historyActions.comment.success, historyActions.cancelEdit, historyActions.delete.success], () => CommentEditState.as.none({})),
    status: createReducer(RequestStatus.INITIAL)
        .handleAction(historyActions.fetch.request, () => RequestStatus.FETCHING)
        .handleAction(historyActions.fetch.success, () => RequestStatus.SUCCESS)
        .handleAction(historyActions.fetch.failure, () => RequestStatus.FAILURE),
    sendStatus: createReducer(RequestStatus.INITIAL)
        .handleAction(historyActions.comment.request, () => RequestStatus.FETCHING)
        .handleAction(historyActions.comment.failure, () => RequestStatus.FAILURE)
        .handleAction(historyActions.comment.success, () => RequestStatus.SUCCESS),
    filters: createReducer<HistoryFilters>({filterType: ['comments', 'files'], type: 'project', projectId: 0})
        .handleAction(historyActions.fetch.request, payloadReducer),
    offset: createReducer(0)
        .handleAction(historyActions.fetch.request, () => 0)
        .handleAction(historyActions.append, (state, {payload}) => state + payload.data.length)
        .handleAction(historyActions.fetch.success, (state) => state + PAGE_SIZE)
        .handleAction(historyActions.deleteComment, (state, {payload}) => state + payload.correction),
    resources: createReducer(EMPTY_RESOURCES)
        .handleAction([historyActions.fetch.success, notificationActions.data.success], (state, {payload}) =>
            concatResources(state, payload.rows.resources))
        .handleAction(historyActions.append, (state, {payload}) => concatResources(state, payload.resources))
        .handleAction(historyActions.comment.success, (state, {payload}) => concatResources(state, {comment: {[payload.id]: payload}}))
        .handleAction(historyActions.delete.success, (state, {payload}) => concatResources(state, {
            comment: R.deleteAt(payload.toString())(state.comment)
        }))
        .handleAction(taskActions.parents.success, (state, action) => concatResources(state, {
            comment: mapComments(action.payload.tasks.flatMap(parent => [...parent.results, ...parent.children?.flatMap(child => child.results) ?? []]))
        }))
        .handleAction(projectActions.get.success, (state, action) => concatResources(state, {
            comment: mapComments(action.payload.project_work_objects.flatMap(object => object.tasks).flatMap(parent => [parent, ...parent.children ?? []]).flatMap(task => task.results))
        }))
        .handleAction(taskActions.comments.success, (state, action) => concatResources(state, {
            comment: mapComments(action.payload.comments)
        }))
        .handleAction(notificationActions.append, (state, action) =>
            action.payload.type === NotificationType.HISTORY_CHANGE ? concatResources(state, action.payload.resources) : state),
    data: createReducer<HistoryData[]>([])
        .handleAction(historyActions.fetch.request, () => [])
        .handleAction(historyActions.append, (state, {payload}) => payload.data.concat(state))
        .handleAction(historyActions.fetch.success, (_, action) => action.payload.rows.data)
        .handleAction(historyActions.deleteComment, (_, action) => action.payload.data)
        .handleAction(historyActions.read.success, (state, {payload}) =>
            entityModify<HistoryData>(entityIdPredicate(payload), historyRead.set(1))(state)
        )
        .handleAction(historyActions.readAll.success, state => state.map(historyRead.set(1)))
        .handleAction(notificationActions.event, (state, action) =>
            entityModify(entityIdPredicate(action.payload.history.id), historyRead.set(action.payload.history.read))(state)
        ),
    owner: createReducer<ServerProject | Project | ProjectWorkObject | ServerTask>(EMPTY_PROJECT)
        .handleAction(historyActions.fetch.success, (_, {payload}) => payload.owner),
    total: createReducer(0)
        .handleAction(historyActions.fetch.request, () => 0)
        .handleAction(historyActions.fetch.success, (_, {payload}) => payload.total)
        .handleAction(historyActions.append, (state, {payload}) => state + payload.data.length)
        .handleAction(historyActions.deleteComment, (state, {payload}) => state + payload.correction),
    editor: createReducer([])
        .handleAction(historyActions.comment.success, (state, action) => action.meta === 'user' ? [] : state)
})

